import { LxClickAbleIcon } from '@components/icon/clickAbleIcon.tsx'
import { EditIcon, MoreActionsIcon, PlusIcon } from '@icons/utils'
import { LxDropdown } from '@components/select/dropdown.tsx'
import styles from './UserActions.module.scss'
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useModal } from '@logic/contexts/Modal/ModalContext.tsx'
import { FilterObject } from '@logic/useFiltering.hook.ts'
import { LxIcon } from '@components/icon/Icon.tsx'
import UserEditStatus from '../CreateorEdit/UserEditStatus'
import { UserEdit } from '../CreateorEdit/UserEdit'
import { UserF } from '@src/logic/contexts/Users/UsersFrontend.type'
import { User } from '@src/logic/contexts/AppStore/UserContext'
import { USER_STATE } from '@src/utils/constants'
import { useNotify } from 'react-admin'

interface ExtendedOption extends FilterObject<ReactNode> {
  disabled?: boolean
  disabledMessage?: string
}

const OptionItem = React.memo<{ icon: ReactNode; label: string; disabled?: boolean }>(({ icon, label, disabled }) => (
  <div className={disabled ? styles.dropdownOptionDisabled : styles.dropdownOption}>
    <LxIcon icon={icon} />
    <span>{label}</span>
  </div>
))

// Memoized options generator
const generateUserOptions = (
  user: UserF,
  userList: UserF[],
  accountId: string,
  accountName: string,
  canEditUser: boolean,
  currentUser: User,
  hasLockFunction?: boolean,
  onUpdated?: () => void
): ExtendedOption[] => {
  const isCurrentUser = currentUser?.id === user.id
  const isUserActive = user.state === USER_STATE.ACTIVE

  const handleStatusUpdated = (reason: string) => {
    if (reason === 'updated') onUpdated?.()
  }

  return [
    // Manage Status
    canEditUser && !isCurrentUser
      ? {
          value: (
            <UserEditStatus
              userId={user.id}
              accountId={accountId}
              accountName={accountName}
              state={user.state}
              userRole={user.userRole}
              userName={user.fullName}
              userList={userList}
              hasLockFunction={hasLockFunction}
              onUpdated={handleStatusUpdated}
            />
          ),
          optionToRender: <OptionItem icon={EditIcon} label='Edit Status' />,
        }
      : {
          value: undefined,
          disabled: true,
          disabledMessage: isCurrentUser ? 'You cannot change your own status' : 'You don’t have permission to change status',
          optionToRender: <OptionItem icon={EditIcon} label='Edit Status' disabled />,
        },

    // Edit Info
    canEditUser && isUserActive
      ? {
          value: <UserEdit user={user} accountId={accountId} accountName={accountName} onUpdated={onUpdated} />,
          optionToRender: <OptionItem icon={EditIcon} label='Edit Info' />,
        }
      : {
          value: undefined,
          disabled: true,
          disabledMessage: 'Info can be edited only when user is active',
          optionToRender: <OptionItem icon={EditIcon} label='Edit Info' disabled />,
        },
  ]
}

interface UserInfoProps {
  user: UserF
  userList: UserF[]
  accountId: string
  accountName?: string
  isLastChild?: boolean
  onUpdated?: () => void
  canEditUser?: boolean
  currentUser?: User | null
  hasLockFunction?: boolean
}

export const UserActions: React.FC<UserInfoProps> = React.memo(({ user, userList, accountId, accountName, canEditUser = false, currentUser, isLastChild, hasLockFunction, onUpdated }) => {
  const [isEditDropdownOpen, setIsEditDropdownOpen] = useState(false)
  const { showModal } = useModal()
  const notify = useNotify()
  const selectRef = useRef<HTMLDivElement>(null)

  const options = useMemo(() => {
    if (!canEditUser || !currentUser) return []
    return generateUserOptions(user, userList, accountId, accountName, canEditUser, currentUser, hasLockFunction, onUpdated)
  }, [user.id, user.state, user.userRole, user.fullName, accountId, canEditUser, currentUser?.id, hasLockFunction, onUpdated])

  useEffect(() => {
    if (!isEditDropdownOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsEditDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isEditDropdownOpen])

  const handleOptionClick = useCallback(
    (option: ExtendedOption) => {
      if (option.disabled) {
        notify(option.disabledMessage || 'Action not available', { type: 'warning' })
        setIsEditDropdownOpen(false)
        return
      }

      if (option.value) {
        showModal(option.value)
        setIsEditDropdownOpen(false)
      }
    },
    [notify, showModal]
  )

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setIsEditDropdownOpen(false)
    }
  }, [])

  const optionRenderer = useCallback((option: ExtendedOption) => option.optionToRender, [])

  if (!canEditUser || !currentUser || options.length === 0) {
    return null
  }

  return (
    <div ref={selectRef} onKeyDown={handleKeyDown} className={styles.UserActions}>
      <LxClickAbleIcon
        icon={MoreActionsIcon}
        dropdown={
          isEditDropdownOpen ? (
            <LxDropdown<ReactNode> className={styles.dropdown} options={options} optionRenderer={optionRenderer} onOptionClick={handleOptionClick} isLastChild={isLastChild} />
          ) : null
        }
        onClick={() => setIsEditDropdownOpen((prev) => !prev)}
      />
    </div>
  )
})
